
import { defineComponent, ref, reactive, onMounted, computed, watch, nextTick } from 'vue'
import { useRoute } from 'vue-router'
import { useStore } from 'vuex'

import { useHead } from '@vueuse/head'

import { RepositoryFactory } from '@/repositories/RepositoryFactory'
const PlayerRepository = RepositoryFactory.get('player')
const SeasonEntryRosterRepository = RepositoryFactory.get('seasonEntryRoster')
const SeasonRepository = RepositoryFactory.get('season')

import BaseBreadcrumb, { Breadcrumb } from '@/components/BaseBreadcrumb.vue'
import BaseDialog from '@/components/BaseDialog.vue'


import { Dialog } from '@/types/dialog'
import { EntryTeam } from '@/types/entryTeam'
import { Player } from '@/types/player'
import { RosterPlayer } from '@/types/rosterPlayer'
import { Season } from '@/types/season'

export default defineComponent({
  name: 'SeasonEntryRoster',
  inheritAttrs: false,
  components: {
    BaseBreadcrumb,
    BaseDialog
  },
  setup() {
    const route = useRoute()
    const store = useStore()

    onMounted(() => {
      getEntryRosters()
      getAllRosters()
      getSeason()
    })

    const rosters: {
      list: Player[]
      selectedEntryPlayer?: RosterPlayer
      selectedPlayer?: Player

      within: RosterPlayer[]
      outside: RosterPlayer[]

      outsideLimit: number
      outsidersTotalNumber: number
    } = reactive({
      list: [],
      selectedEntryPlayer: undefined,
      selectedPlayer: undefined,

      within: [],
      outside: [],

      outsideLimit: 5,
      outsidersTotalNumber: 0
    })

    const entry: {
      data?: EntryTeam
    } = reactive({
      data: undefined
    })

    const getEntryRosters = async () => {
      try {
        const { season_id, team_id } = route.params

        const res = await SeasonEntryRosterRepository.getRosters(season_id, team_id)

        if (res.status === 200) {
          const r = res.data
          const data = r.data

          rosters.list = data.rosters
          entry.data = data.entry_team
        }
      } catch (error) {
        //
      }
    }

    const getAllRosters = async () => {
      try {
        const { season_id, team_id } = route.params

        const dataObj = {
          keyword: searchForm.keyword
        }

        const res = await SeasonEntryRosterRepository.getAllRosters(season_id, team_id, dataObj)

        if (res.status === 200) {
          const r = res.data
          const data = r.data

          rosters.within = data.within
          rosters.outside = data.outside.slice(0, rosters.outsideLimit)

          rosters.outsidersTotalNumber = data.outsiders_total_number - rosters.outsideLimit
        }
      } catch (error) {
        //
      }
    }

    const searchForm = reactive({
      keyword: ''
    })

    watch(
      () => searchForm,
      () => {
        getAllRosters()
      },
      { deep: true }
    )

    const isSearching = computed(() => {
      if (searchForm.keyword !== '') {
        return true
      } else {
        return false
      }
    })

    const selectEntryPlayer = (player: Player) => {
      if (player == undefined) return
      playerFormRegister.player_id = player.id
      playerFormRegister.number = ''
      playerFormRegister.name = player.name

      nextTick(() => {
        (numberInputElement.value as HTMLInputElement).focus()
      })
    }

    const playerDialogMoveConfirm = reactive<Dialog>({
      title: '{選手名}を{チーム名}に移籍しますか？',
      message: '注意書き',
      confirmText: '移籍する',
      cancelText: 'キャンセル',

      isOpen: false
    })

    const openPlayerDialogMoveConfirm = (player: Player) => {
      rosters.selectedPlayer = player
      playerDialogMoveConfirm.title = player.name + ' を ' + entry.data?.team_name + ' に移籍しますか？'

      playerDialogMoveConfirm.isOpen = true
    }

    const closePlayerDialogMoveConfirm = () => {
      playerDialogMoveConfirm.isOpen = false
    }


    const movePlayer = async () => {
      try {
        const dataObj = {
          team_id: entry.data?.team_id,
          player_id: rosters.selectedPlayer?.id,
        }

        const res = await PlayerRepository.movePlayer(dataObj)

        if (res.status === 200) {
          const r = res.data
          const data = r.data

          getAllRosters()

          playerFormRegister.player_id = data.player.id
          playerFormRegister.number = ''
          playerFormRegister.name = data.player.name

          closePlayerDialogMoveConfirm()

          store.dispatch('addFlashMessage', {
            type: 'success',
            message: '選手の所属チームを変更しました'
          })

          nextTick(() => {
            (numberInputElement.value as HTMLInputElement).focus()
          })
        }
      } catch (error) {
        //
      }
    }

    const playerFormAdd = reactive({
      name: '',
      isCompleted: true
    })

    watch(
      () => [playerFormAdd.name],
      () => {
        playerFormAdd.isCompleted = (playerFormAdd.name !== '') ? true : false
      },
      { immediate: true },
    )

    const addPlayerToMasio = async () => {
      try {
        const dataObj = {
          team_id: entry.data?.team_id,
          name: playerFormAdd.name,
        }

        const res = await PlayerRepository.postPlayer(dataObj)

        if (res.status === 200) {
          const r = res.data
          const data = r.data

          playerFormAdd.name = ''
          getAllRosters()

          playerFormRegister.player_id = data.new.id
          playerFormRegister.number = ''
          playerFormRegister.name = data.new.name

          nextTick(() => {
            (numberInputElement.value as HTMLInputElement).focus()
          })
        }
      } catch (error) {
        //
      }
    }

    const playerFormRegister: {
      player_id?: number
      number: number | string
      name: string
    } = reactive({
      player_id: undefined,
      number: '',
      name: ''
    })
    const numberInputElement = ref<HTMLInputElement>()

    const showErrorMessageUniformNumber = () => {
      store.dispatch('addFlashMessage', {
        type: 'danger',
        message: '数字を入力してください'
      })
    }

    const onChangeUniformNumber = (event: Event, type: 'add' | 'update', player: RosterPlayer) => {
      const target = event.target as HTMLInputElement
      const value = target.value
      const halfSizeNumber = value.replace(/[０-９]/g, (s: any) => String.fromCharCode(s.charCodeAt(0) - 65248)).replace(/\D/g,'')

      switch (type) {
      case 'add':
        playerFormRegister.number = halfSizeNumber
        if (halfSizeNumber.length === 0) {
          showErrorMessageUniformNumber()
          return
        } else {
          addPlayerToSeason()
        }
        break
      case 'update':
        if (halfSizeNumber.length === 0) {
          showErrorMessageUniformNumber()
          getEntryRosters()
        } else {
          player.number = Number(halfSizeNumber)
          updateUniformNumber(player)
        }
        break
      default:
        break
      }
    }

    const addPlayerToSeason = async () => {
      try {
        const { season_id, team_id } = route.params

        const dataObj = {
          player_id: playerFormRegister.player_id,
          number: playerFormRegister.number
        }

        const res = await SeasonEntryRosterRepository.postPlayer(season_id, team_id, dataObj)

        if (res.status === 200) {
          const r = res.data
          const data = r.data

          playerFormRegister.player_id = undefined
          playerFormRegister.number = ''
          playerFormRegister.name = ''

          rosters.list = data.rosters
          getAllRosters()

          store.dispatch('addFlashMessage', {
            type: 'success',
            message: '選手を登録しました'
          })
        }
      } catch (error) {
        //
      }
    }

    const updateUniformNumber = async (player: RosterPlayer) => {
      try {
        const { season_id, team_id } = route.params

        const dataObj = {
          player_id: player.player_id,
          number: player.number
        }

        const res = await SeasonEntryRosterRepository.updatePlayer(season_id, team_id, dataObj)

        if (res.status === 200) {
          const r = res.data
          const data = r.data

          rosters.list = data.rosters
          getAllRosters()

          store.dispatch('addFlashMessage', {
            type: 'success',
            message: '登録選手の背番号を更新しました'
          })
        }
      } catch (error) {
        //
      }
    }

    const season: {
      data?: Season
    } = reactive({
      data: undefined
    })

    const getSeason = async () => {
      try {
        const { season_id } = route.params

        const res = await SeasonRepository.getSeason(season_id)

        if (res.status === 200) {
          const r = res.data
          const data = r.data

          season.data = data.season
        }
      } catch (error) {
        //
      }
    }

    const playerDialogDeleteFromSeason = reactive<Dialog>({
      title: '{選手名}を{イベント名名}{シーズン名}から削除しますか？',
      message: '注意書き',
      confirmText: '削除する',
      cancelText: 'キャンセル',

      isOpen: false
    })

    const openPlayerDialogDeleteFromSeason = (player: RosterPlayer) => {
      rosters.selectedEntryPlayer = player
      playerDialogDeleteFromSeason.title = `${player.player_name} を ${season.data?.topic_name}${season.data?.name} から削除しますか？`

      playerDialogDeleteFromSeason.isOpen = true
    }

    const closePlayerDialogDeleteFromSeason = () => {
      playerDialogDeleteFromSeason.isOpen = false
    }


    const deletePlayerFromSeason = async () => {
      try {
        const { season_id, team_id } = route.params

        const dataObj = {
          player_id: rosters.selectedEntryPlayer?.player_id,
          number: rosters.selectedEntryPlayer?.number
        }

        const res = await SeasonEntryRosterRepository.deletePlayer(season_id, team_id, dataObj)

        if (res.status === 200) {
          getEntryRosters()
          getAllRosters()

          closePlayerDialogDeleteFromSeason()

          store.dispatch('addFlashMessage', {
            type: 'success',
            message: '選手をシーズンから削除しました'
          })
        }
      } catch (error) {
        //
      }
    }

    useHead({
      title: computed(() => '登録選手 - ' + entry.data?.team_name + ' - ' + season.data?.topic_name + season.data?.name),
    })

    const breadcrumbs = computed(() => {
      return [
        {
          type: 'link',
          link: {
            name: '大会一覧',
            path: '/'
          }
        },
        {
          type: 'link',
          size: 'large',
          link: {
            name: season.data?.topic_name,
            secondName: season.data?.name,
            path: '/event/' + route.params.topic_id + '/season/' + route.params.season_id,
          },
          setting: {
            path: '/event/' + route.params.topic_id
          }
        },
        {
          type: 'link',
          link: {
            name: 'エントリーチーム',
            path: '/event/' + route.params.topic_id + '/season/' + route.params.season_id + '/team'
          }
        },
        {
          type: 'link',
          link: {
            name: entry.data?.team_name,
            path: '/event/' + route.params.topic_id + '/season/' + route.params.season_id + '/team/' + route.params.team_id,
          }
        },
        {
          type: 'text',
          text: {
            name: '登録選手'
          }
        },
      ] as Breadcrumb[]
    })

    return {
      rosters,
      entry,
      getEntryRosters,
      getAllRosters,

      searchForm,
      isSearching,
      selectEntryPlayer,

      playerDialogMoveConfirm,
      openPlayerDialogMoveConfirm,
      closePlayerDialogMoveConfirm,
      movePlayer,

      playerFormAdd,
      addPlayerToMasio,

      playerFormRegister,
      numberInputElement,
      onChangeUniformNumber,
      addPlayerToSeason,
      updateUniformNumber,

      playerDialogDeleteFromSeason,
      openPlayerDialogDeleteFromSeason,
      closePlayerDialogDeleteFromSeason,
      deletePlayerFromSeason,

      breadcrumbs
    }
  }
})
